import React, { useState, useEffect, useRef } from 'react';
import { getBrandName } from 'utils/brand';
import classNames from 'classnames';
import css from './style.scss';
import {
  closePopup,
  getPopupRegistration,
  openPopup,
  Priority
} from 'app/PopupHolder';
import ListManagementField from '../../../components/ListManagementField';
import infoGray from 'assets/images/info-gray.svg';
import { useSelector, useDispatch } from "react-redux";
import ActivateSubscriptionPopup from '../../AccountNew/modals/ActivateSubscription';
import Link from 'components/Link';
import { selectPlanDetails, selectUserId } from '../../../data/user';
import { useToast } from '../ToastMessage/ToastManager';
import { selectLoading, selectImportLoading } from '../../../data/property';
import { selectLoading as selectContactLoading } from '../../../data/contacts';
import SVG from 'components/base/SVG';
import Tooltip from '../Tooltip';
import { getUsageSectionAPI } from '../../../data/user/actions';
import CheckboxItem from '../../../components/Checkbox';

const BODY_CLASS_NAME = 'bodyModal';

const AddToMarketingListModal = ({
  title = 'Add to Marketing List',
  checkboxLabel= "Skip Trace Selected Properties",
  groups = [],
  closePopup,
  onSave,
  onCancel,
  initialList = '',
  hideSkipTraceOption,
  type = 'property',
  loading : apiLoading,
  contactImportMode,
  checkOptions = null, 
}) => {
  const [selectedList, setSelectedList] = useState(initialList);
  const [skipTrace, setSkipTrace] = useState(false);
  const modalRef = useRef(null);
  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const [showActivatePopup, setShowActivatePopup] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState(false);
  const showToast = useToast();
  const savePropertyLoading =  useSelector(selectLoading);
  const propertyImportLoading = useSelector(selectImportLoading);
  const contactImportLoading = useSelector(selectContactLoading);
  const userID = useSelector(selectUserId);
  const loading = apiLoading || savePropertyLoading || propertyImportLoading || contactImportLoading;
  const handleClose = () => {
    document.body.classList.remove(BODY_CLASS_NAME);
    closePopup?.();
  };
  const dispatch = useDispatch();

  useEffect(() => {
    document.body.classList.add(BODY_CLASS_NAME);
    const handleKeyDown = (e) => e.key === 'Escape' && handleClose();
    const handleClickOutside = (e) => {
      // if (e.target.closest('.modal-content')) {
      //   return;
      // }

      // if (modalRef.current && !modalRef.current.contains(e.target)) {
      //   handleClose();
      // }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.body.classList.remove(BODY_CLASS_NAME);
    };
  }, []);

  const handleSave = () => {
    if (!selectedList) return;
    const isCreated = !selectedList.id;

    if (onSave) {
      try {
        // Build the third parameter object based on what's available
        const saveOptions = {
          skipTrace,
          isCreated,
          id: selectedList.id || null,
        };
        
        // Only add checkOptions if delete is selected AND checkOptions was provided
        if (checkOptions && checkOptions.length > 0 && deleteSelected) {
          saveOptions.checkOptions = checkOptions;
        }
        
        onSave(
          selectedList.name || selectedList,
          // `action` is an optional 4th arg — { label, onClick } — additive/opt-in;
          // callers that don't pass it are unaffected (showToast renders no button).
          (savedQuantity, message, error, action) => {
            if (error || message === 'error') {
              showToast('Something went wrong. Please try again.', 'error');
            } else if (savedQuantity != null) {
              const listName = selectedList.name || selectedList;
              dispatch(getUsageSectionAPI(userID));
              const savedTypeLabel = type === "contacts" ? "contacts" : type === "investor" ? "investors" : "properties";
              showToast(`${savedQuantity || 0} ${savedTypeLabel} saved to "${listName}"`, 'success', action);
            }
            handleClose();
          },
          saveOptions
        );
      } catch (err) {
        showToast('Something went wrong. Please try again.', 'error');
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    handleClose();
  };

  const handleUpgradeClick = (e) => {
     setShowActivatePopup(true);
  };

  return (
    <div className={css.modalOverlay}>
      <div className={css.modalWrapper}>
        <div
          ref={modalRef}
          className={classNames(css.modalBox, 'modal-content', { [css.clsModalBodyHiddenSkipTrace]: !!hideSkipTraceOption})}
        >
          <div className={css.modal_header}>
            <h3 className={css.modal_title}>{title}</h3>
              {
                !contactImportMode && <>
                  {planDetails?.trialAvailable &&
                    <div className={css.trial_message}>
                      You&apos;re on a trial with 50 saved properties. Upgrade now to access the full 25,000.&nbsp;
                      <Link className={css.link}
                        onClick={handleUpgradeClick}
                      >Upgrade Now</Link>
                    </div>
                  }
                </>
              }
          </div>

          <div className={css.modal_body}>
            <ListManagementField
              lists={groups}
              onSelect={(list) => setSelectedList(list)}
              onCreate={(name) => setSelectedList({ name })}
              placeholder="Select or Type to Create a New List"
            />
            {/* Conditionally show delete checkbox if checkOptions is provided */}
            {checkOptions && checkOptions.length > 0 && (
              <div className={css.checkbox_row}>
                <CheckboxItem
                  label={checkOptions[0].label}
                  checked={deleteSelected}
                  onChange={() => setDeleteSelected(!deleteSelected)}
                  labelClassName={css.checkBoxLabelClassName}
                />
              </div>
            )}
            {!hideSkipTraceOption && <div className={css.checkbox_row}>
              <CheckboxItem
                label={checkboxLabel}
                checked={skipTrace}
                onChange={() => setSkipTrace(!skipTrace)}
                labelClassName={css.checkBoxLabelClassName}
              />
              <div className={css.info_wrapper}>
                  <Tooltip title="Skip Trace"
                    description={`Skip Tracing finds phone numbers and emails for your prospects so you can call, text, or email them using ${getBrandName()}’s marketing tools.`}
                    position="bottom"
                    bgColor="#128292"
                    width={312}
                    xOffset={125}
                    yOffset={-10}
                    arrowOffset={30}
                  >
                    <img className={css.clsInfoIconImg} src={infoGray} alt="info" />
                  </Tooltip>
              </div>
            </div>}
          </div>

          <div className={css.modal_footer}>
            <button
              type="button"
              className={classNames(css.btn, css.btn_secondary, { [css.disabledBtn]: loading })}
              onClick={handleCancel}
              disabled={loading}
            >
             Cancel
            </button>

            <button
              type="button"
              className={classNames(css.btn, css.btn_primary, { [css.disabledBtn]: !selectedList || loading })}
              onClick={handleSave}
              disabled={!selectedList || loading}
            >
              {loading ? (
                <SVG icon="spinner" className={classNames(css.spinner)} />
              ) :
                "Save"}
            </button>
          </div>
        </div>
      </div>
      {showActivatePopup && (
         <ActivateSubscriptionPopup closePopup={() => setShowActivatePopup(false)} />
      )}
    </div>
  );
};

const registrationId = getPopupRegistration(AddToMarketingListModal);
// `priority` defaults to MEDIUM (this modal's usual tier) but stays
// overridable — a caller opening it on top of another already-open MEDIUM
// popup (e.g. the Investor Profile modal) needs to pass a higher one, same
// as EditContactModal.js does for the same reason.
AddToMarketingListModal.open = (props = {}) =>
openPopup(registrationId, { priority: Priority.MEDIUM, ...props });
AddToMarketingListModal.close = () => closePopup({ popup: registrationId });

export default AddToMarketingListModal;
